import { useState } from 'react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { UserCourseProgressDto } from '@prometeus/contracts';

type QuizQuestion = {
  id: string;
  stem: string;
  type: string;
  options: { id: string; text: string; order: number }[];
};

export const Route = createFileRoute('/certificacao/cursos/$courseId/simulado')({
  component: SimuladoPage,
});

function SimuladoPage() {
  const { courseId } = useParams({ from: '/certificacao/cursos/$courseId/simulado' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [started, setStarted] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ score: number; passed: boolean; certificateId?: string } | null>(null);

  const { data: progress } = useQuery<UserCourseProgressDto | null>({
    queryKey: ['certification-progress', courseId],
    queryFn: async () => {
      try {
        const response = await api.get(`/certification/courses/${courseId}/progress`);
        return response.data?.data ?? response.data;
      } catch {
        return null;
      }
    },
    enabled: !!courseId,
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/certification/courses/${courseId}/quiz/start`);
      return response.data?.data ?? response.data;
    },
    onSuccess: (data: { attemptId: string; questions: QuizQuestion[] }) => {
      setAttemptId(data.attemptId);
      setQuestions(data.questions ?? []);
      setStarted(true);
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/certification/courses/${courseId}/quiz/submit`, {
        attemptId,
        answers,
      });
      return response.data?.data ?? response.data;
    },
    onSuccess: (data: { score: number; passed: boolean; certificateId?: string }) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['certification-progress', courseId] });
      queryClient.invalidateQueries({ queryKey: ['certification-course', courseId] });
    },
  });

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id]);

  if (result !== null) {
    const isWorkspaceCompletion = result.passed && !result.certificateId;
    return (
      <div
        className="rounded-[10px] bg-white dark:bg-gray-800 p-6 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
      >
        <h2 className="text-base font-semibold text-[#06141B] dark:text-white mb-3">Resultado do simulado</h2>
        <p className="text-2xl font-bold text-[#06141B] dark:text-white mb-2">{result.score}%</p>
        <p className="text-sm text-[#253745] dark:text-gray-400 mb-5">
          {result.passed
            ? isWorkspaceCompletion
              ? 'Aprovado! Você concluiu a certificação do workspace.'
              : 'Aprovado! Parabéns.'
            : 'Reprovado. Nota mínima: 90%. Tente novamente.'}
        </p>
        {result.passed && result.certificateId && (
          <button
            type="button"
            onClick={() =>
              navigate({
                to: '/certificacao/certificados/$id',
                params: { id: result.certificateId! },
              })
            }
            className="px-4 py-2 text-sm rounded-[10px] bg-[#06141B] text-white font-medium hover:bg-[#11212D] transition-colors focus:outline-none focus:ring-0"
          >
            Ver certificado
          </button>
        )}
        {!result.passed && (
          <button
            type="button"
            onClick={() => {
              setResult(null);
              setStarted(false);
              setAttemptId(null);
              setQuestions([]);
              setAnswers({});
              startMutation.mutate();
            }}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#9BA8AB]/20 dark:bg-gray-700/50 text-[#06141B] dark:text-gray-300 font-medium hover:bg-[#9BA8AB]/30 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-0"
          >
            Nova tentativa
          </button>
        )}
      </div>
    );
  }

  if (progress?.status === 'certified') {
    const hasCertificate = progress?.certificateId != null;
    return (
      <div
        className="rounded-[10px] bg-white dark:bg-gray-800 p-6 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
      >
        <h2 className="text-base font-semibold text-[#06141B] dark:text-white mb-2">Você já foi aprovado neste curso</h2>
        <p className="text-sm text-[#253745] dark:text-gray-400 mb-5">
          {hasCertificate ? 'Seu certificado já foi emitido. Acesse-o pelo botão abaixo.' : 'Você concluiu a certificação do workspace.'}
        </p>
        {hasCertificate && (
          <button
            type="button"
            onClick={() =>
              navigate({
                to: '/certificacao/certificados/$id',
                params: { id: progress.certificateId! },
              })
            }
            className="px-4 py-2 text-sm rounded-[10px] bg-[#06141B] text-white font-medium hover:bg-[#11212D] transition-colors focus:outline-none focus:ring-0"
          >
            Ver certificado
          </button>
        )}
      </div>
    );
  }

  if (!started) {
    return (
      <div
        className="rounded-[10px] bg-white dark:bg-gray-800 p-6 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
      >
        <h2 className="text-base font-semibold text-[#06141B] dark:text-white mb-2">Simulado</h2>
        <p className="text-sm text-[#253745] dark:text-gray-400 mb-5">
          Você concluiu todas as etapas. Inicie o simulado para obter o certificado. Nota mínima: 90%.
        </p>
        <button
          type="button"
          onClick={() => startMutation.mutate()}
          disabled={startMutation.isPending}
          className="px-4 py-2 text-sm rounded-[10px] bg-[#06141B] text-white font-medium hover:bg-[#11212D] disabled:opacity-50 transition-colors focus:outline-none focus:ring-0"
        >
          {startMutation.isPending ? 'Iniciando...' : 'Iniciar simulado'}
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-[10px] bg-white dark:bg-gray-800 overflow-hidden"
      style={{ fontFamily: "'Poppins', sans-serif", boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
    >
      <div className="p-6 border-b border-[#9BA8AB]/30 dark:border-gray-600">
        <h2 className="text-base font-semibold text-[#06141B] dark:text-white">Simulado</h2>
        <p className="text-sm text-[#9BA8AB] mt-0.5">
          Responda todas as questões e envie ao final.
        </p>
      </div>
      <div className="p-6 space-y-6">
        {questions.map((q, index) => (
          <div
            key={q.id}
            className="rounded-[10px] p-4 bg-gray-50/50 dark:bg-gray-700/30"
          >
            <p className="font-semibold text-[#06141B] dark:text-white mb-3" style={{ fontSize: 16 }}>
              {index + 1}. {q.stem}
            </p>
            <div className="space-y-2">
              {(q.options ?? [])
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((opt) => (
                  <label
                    key={opt.id}
                    className="flex items-center gap-2 cursor-pointer p-2 rounded-xl hover:bg-[#9BA8AB]/10 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <input
                      type="radio"
                      name={q.id}
                      value={opt.id}
                      checked={answers[q.id] === opt.id}
                      onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt.id }))}
                      className="rounded-full border-[#4A5C6A]/50 focus:outline-none focus:ring-0"
                    />
                    <span className="text-[#06141B] dark:text-gray-300" style={{ fontSize: 12 }}>{opt.text}</span>
                  </label>
                ))}
            </div>
          </div>
        ))}
      </div>
      <div className="p-6 border-t border-[#9BA8AB]/30 dark:border-gray-600 flex justify-end">
        <button
          type="button"
          onClick={() => submitMutation.mutate()}
          disabled={!allAnswered || submitMutation.isPending}
          className="px-4 py-2 text-sm rounded-[10px] bg-[#06141B] text-white font-medium hover:bg-[#11212D] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-0"
        >
          {submitMutation.isPending ? 'Enviando...' : 'Enviar simulado'}
        </button>
      </div>
    </div>
  );
}
